// URL normalization utility for handling language detection and legacy redirects

// Define legacy URL mappings
const LEGACY_REDIRECTS = {
  '/about-4': '/nossa-historia',
  '/contact-8': '/como-chegar', 
  '/a-pousada': '/lodge',
  '/rooms': '/acomodacoes',
  '/the-lodge': '/lodge',
  '/localizacao': '/como-chegar',
  '/location': '/como-chegar',
  '/restaurante': '/gastronomia',
  '/restaurant': '/gastronomia',
  '/reserve-um-quarto': '/acomodacoes',
  '/top-hotspot-birdwatching': '/birdwatching',
  '/estacaodepesquisa': '/estacao-pesquisa',
  '/suldamazonia': '/',
  '/_frog': '/',
  '/bolt-performance': '/',
  '/general-9': '/',
  '/general-6': '/'
};

const SUPPORTED_LANGUAGES = ['en', 'pt'];
const DEFAULT_LANGUAGE = 'pt'; // Changed to Portuguese as default
const BASE_URL = 'https://jardimdaamazonia.com';

// English URL slug mappings for better UX
const ENGLISH_SLUGS = {
  'acomodacoes': 'accommodations',
  'gastronomia': 'gastronomy', 
  'como-chegar': 'how-to-get-here',
  'guias-campo': 'field-guides',
  'estacao-pesquisa': 'research-station',
  'nascente-natural': 'natural-spring',
  'nossa-historia': 'our-story',
  'falaram-de-nos': 'media-press',
  'eventos-natureza': 'nature-events',
  'galeria': 'gallery',
  'trilhas': 'trails'
};

// Reverse mapping for converting English back to Portuguese
const PORTUGUESE_SLUGS = Object.fromEntries(
  Object.entries(ENGLISH_SLUGS).map(([pt, en]) => [en, pt])
);

/**
 * Extract language from current URL (supports both old and new formats)
 */
export const extractLanguageFromUrl = () => {
  const currentPath = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  
  // Check for new path-based language format: /en/page or /pt/page
  const pathSegments = currentPath.split('/').filter(Boolean);
  if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0])) {
    return {
      language: pathSegments[0],
      path: `/${pathSegments.slice(1).join('/')}`,
      format: 'path'
    };
  }
  
  // Check for old query parameter format: /page?lang=en
  const queryLang = searchParams.get('lang');
  if (queryLang && SUPPORTED_LANGUAGES.includes(queryLang)) {
    return {
      language: queryLang,
      path: currentPath,
      format: 'query'
    };
  }
  
  // No language detected, use default
  return {
    language: DEFAULT_LANGUAGE,
    path: currentPath,
    format: 'none'
  };
};

/**
 * Get localized slug for a path based on language
 */
export const getLocalizedSlug = (path, language) => {
  const cleanPath = path.replace(/^\//, '').replace(/\/$/, '');
  
  if (language === 'en' && ENGLISH_SLUGS[cleanPath]) {
    return ENGLISH_SLUGS[cleanPath];
  } else if (language === 'pt' && PORTUGUESE_SLUGS[cleanPath]) {
    return PORTUGUESE_SLUGS[cleanPath];
  }
  
  return cleanPath;
};

/**
 * Convert old query parameter URLs to new path-based URLs
 */
export const convertToNewUrlFormat = (path, language) => {
  // Remove any existing language prefix
  const cleanPath = path.replace(/^\/(en|pt)\//, '/');
  
  // Handle root path
  if (cleanPath === '/' || cleanPath === '') {
    return `/${language}/`;
  }
  
  // Get the path without leading slash
  const pathWithoutSlash = cleanPath.replace(/^\//, '');
  
  // Use localized slug for English
  const localizedPath = language === 'en' && ENGLISH_SLUGS[pathWithoutSlash] 
    ? ENGLISH_SLUGS[pathWithoutSlash] 
    : pathWithoutSlash;
  
  // Add language prefix to path
  return `/${language}/${localizedPath}`;
};

/**
 * Generate the new URL format for a given path and language
 */
export const generateNewUrl = (path, language = DEFAULT_LANGUAGE) => {
  const newPath = convertToNewUrlFormat(path, language);
  return `${BASE_URL}${newPath}`;
};

/**
 * Check if current URL needs normalization and redirect if necessary
 */
export const normalizeCurrentUrl = () => {
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  const searchParams = new URLSearchParams(currentSearch);
  
  // Check if this is a legacy URL that needs redirection
  if (LEGACY_REDIRECTS[currentPath]) {
    const targetPath = LEGACY_REDIRECTS[currentPath];
    const currentLang = searchParams.get('lang') || DEFAULT_LANGUAGE;
    
    // Redirect to new path-based format
    const newUrl = convertToNewUrlFormat(targetPath, currentLang);
    window.history.replaceState({}, '', newUrl);
    return true;
  }
  
  // Check for old query parameter format and convert to new format
  const langInfo = extractLanguageFromUrl();
  
  if (langInfo.format === 'query') {
    // Convert query parameter format to path-based format
    const newUrl = convertToNewUrlFormat(langInfo.path, langInfo.language);
    window.history.replaceState({}, '', newUrl);
    return true;
  }
  
  if (langInfo.format === 'none' && currentPath !== '/') {
    // Add language prefix to pages without language
    const newUrl = convertToNewUrlFormat(currentPath, DEFAULT_LANGUAGE);
    window.history.replaceState({}, '', newUrl);
    return true;
  }
  
  return false;
};

/**
 * Get the canonical URL for the current page
 */
export const getCanonicalUrl = (pathname, currentLang) => {
  // Remove any existing language prefix
  const cleanPath = pathname.replace(/^\/(en|pt)\//, '/');
  
  // For the homepage, use the base URL
  if (cleanPath === '/' || cleanPath === '') {
    return `${BASE_URL}/${currentLang}/`;
  }
  
  // For other pages, use the language-specific URL
  const lang = (currentLang && SUPPORTED_LANGUAGES.includes(currentLang)) 
    ? currentLang 
    : DEFAULT_LANGUAGE;
  
  return `${BASE_URL}/${lang}${cleanPath}`;
};

/**
 * Get hreflang URLs for the current page
 */
export const getHreflangUrls = (pathname) => {
  // Remove any existing language prefix
  const cleanPath = pathname.replace(/^\/(en|pt)\//, '/');
  const urls = {};
  
  // For the homepage
  if (cleanPath === '/' || cleanPath === '') {
    urls['en'] = `${BASE_URL}/en/`;
    urls['pt'] = `${BASE_URL}/pt/`;
    urls['x-default'] = `${BASE_URL}/pt/`; // Changed to Portuguese as default
    return urls;
  }
  
  // For other pages
  for (const lang of SUPPORTED_LANGUAGES) {
    urls[lang] = `${BASE_URL}/${lang}${cleanPath}`;
  }
  
  urls['x-default'] = `${BASE_URL}/pt${cleanPath}`; // Changed to Portuguese as default
  
  return urls;
};

/**
 * Detect user's preferred language from browser
 */
export const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
  
  const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage];
  
  for (const lang of browserLanguages) {
    const primaryLang = lang.split('-')[0].toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(primaryLang)) {
      return primaryLang;
    }
  }
  
  return DEFAULT_LANGUAGE;
};

/**
 * Generate language-aware URLs for navigation
 */
export const createLanguageAwareUrl = (path, currentLang) => {
  const lang = (currentLang && SUPPORTED_LANGUAGES.includes(currentLang)) 
    ? currentLang 
    : DEFAULT_LANGUAGE;
  
  return convertToNewUrlFormat(path, lang);
};

/**
 * Check if a URL is a legacy redirect target
 */
export const isLegacyUrl = (pathname) => {
  return Object.prototype.hasOwnProperty.call(LEGACY_REDIRECTS, pathname);
};

/**
 * Get redirect destination for legacy URL
 */
export const getLegacyRedirectTarget = (pathname) => {
  return LEGACY_REDIRECTS[pathname] || null;
};

/**
 * Get current language from URL
 */
export const getCurrentLanguage = () => {
  const langInfo = extractLanguageFromUrl();
  return langInfo.language;
};

/**
 * Get current path without language prefix
 */
export const getCurrentPath = () => {
  const langInfo = extractLanguageFromUrl();
  return langInfo.path;
}; 